// Four React Flow custom node components — one per flowchart shape.
// All live in a single file so they share the small NodeShell wrapper
// (border, label, handles) without leaking implementation between
// each other. Designer + Viewer plug these into the `nodeTypes` map.

import React from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { NODE_VISUAL_PRESETS, type FlowchartNodeType } from './types';

interface NodeData {
  label: string;
  // When true the node renders its handles smaller/dimmer — used by
  // the read-only Viewer so users don't think they can connect.
  readOnly?: boolean;
}

// Compact reusable shell — handles + label rendering — so each shape
// only worries about its own clip-path / borderRadius.
function NodeShell({
  type,
  data,
  children,
}: {
  type: FlowchartNodeType;
  data: NodeData;
  children: React.ReactNode;
}) {
  const preset = NODE_VISUAL_PRESETS[type];
  const handleStyle: React.CSSProperties = {
    width: 10, height: 10,
    background: preset.border,
    border: '2px solid #FFFFFF',
    visibility: data.readOnly ? 'hidden' : 'visible',
  };
  // Four-sided handles so both vertical (top↔bottom) and horizontal
  // RTL (right→left) layouts can connect cleanly. Auto-generated
  // diagrams use 'right' (source) → 'left' (target) to match the
  // manual's right-to-left flow; manual user connections still work
  // off the default top/bottom handles. Each side has BOTH a source
  // and a target handle stacked at the same position, so users can
  // drag a connection in either direction without thinking about it.
  return (
    <div style={{
      width: preset.width,
      height: preset.height,
      position: 'relative',
    }}>
      <Handle id="top"          type="target" position={Position.Top}    style={handleStyle} />
      <Handle id="top-source"   type="source" position={Position.Top}    style={handleStyle} />
      <Handle id="bottom"       type="source" position={Position.Bottom} style={handleStyle} />
      <Handle id="bottom-target" type="target" position={Position.Bottom} style={handleStyle} />
      <Handle id="left"         type="target" position={Position.Left}   style={handleStyle} />
      <Handle id="left-source"  type="source" position={Position.Left}   style={handleStyle} />
      <Handle id="right"        type="source" position={Position.Right}  style={handleStyle} />
      <Handle id="right-target" type="target" position={Position.Right}  style={handleStyle} />
      {children}
    </div>
  );
}

function PillBody({ label, preset }: { label: string; preset: typeof NODE_VISUAL_PRESETS.start }) {
  return (
    <div style={{
      width: '100%', height: '100%',
      background: preset.bg,
      border: `2px solid ${preset.border}`,
      borderRadius: 999,
      display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      padding: '4px 16px',
      color: preset.text,
      fontSize: 13, fontWeight: 600,
      textAlign: 'center',
      direction: 'rtl',
      lineHeight: 1.25,
      // Long labels truncate visually rather than push the diagram
      // around — users can hover to see the full title.
      overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
    }} title={label}>
      {label}
    </div>
  );
}

function RectBody({ label, preset }: { label: string; preset: typeof NODE_VISUAL_PRESETS.step }) {
  return (
    <div style={{
      width: '100%', height: '100%',
      background: preset.bg,
      border: `2px solid ${preset.border}`,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      padding: '8px 14px',
      color: preset.text,
      fontSize: 13, fontWeight: 600,
      textAlign: 'center',
      direction: 'rtl',
      lineHeight: 1.3,
      wordBreak: 'break-word',
    }} title={label}>
      {label}
    </div>
  );
}

function DiamondBody({ label, preset }: { label: string; preset: typeof NODE_VISUAL_PRESETS.decision }) {
  // Diamonds are tricky because React Flow's bounding box is a
  // rectangle. Inner div is rotated 45° and the text is rotated back
  // so users read it horizontally. Keeps the connection handles on
  // the top/bottom corners of the diamond.
  return (
    <div style={{
      width: '100%', height: '100%',
      position: 'relative',
    }} title={label}>
      <div style={{
        position: 'absolute',
        top: '15%', left: '15%', width: '70%', height: '70%',
        background: preset.bg,
        border: `2px solid ${preset.border}`,
        transform: 'rotate(45deg)',
      }} />
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        padding: '0 18px',
        color: preset.text,
        fontSize: 12, fontWeight: 600,
        textAlign: 'center',
        direction: 'rtl',
        lineHeight: 1.2,
        pointerEvents: 'none',
      }}>
        {label}
      </div>
    </div>
  );
}

function makeNodeComponent(type: FlowchartNodeType) {
  const preset = NODE_VISUAL_PRESETS[type];
  const Component: React.FC<NodeProps> = ({ data }) => {
    const nodeData = data as NodeData;
    const label = nodeData.label || '';
    return (
      <NodeShell type={type} data={nodeData}>
        {preset.shape === 'pill' && <PillBody label={label} preset={preset} />}
        {preset.shape === 'rect' && <RectBody label={label} preset={preset} />}
        {preset.shape === 'diamond' && <DiamondBody label={label} preset={preset} />}
      </NodeShell>
    );
  };
  Component.displayName = `Flowchart${type[0].toUpperCase()}${type.slice(1)}Node`;
  return Component;
}

export const StartNode    = makeNodeComponent('start');
export const StepNode     = makeNodeComponent('step');
export const DecisionNode = makeNodeComponent('decision');
export const EndNode      = makeNodeComponent('end');

// nodeTypes map for React Flow — same instance for designer + viewer
// so React Flow's referential-equality optimisations stay happy.
export const FLOWCHART_NODE_TYPES_MAP = {
  start:    StartNode,
  step:     StepNode,
  decision: DecisionNode,
  end:      EndNode,
};
